import { useState } from 'react';
import { formatearCLP } from '@lexicore/shared';
import { IconoCheck, IconoCerrar, IconoDocumento } from '../../componentes/Iconos';
import { useAvisos } from '../../contextos/AvisosContext';
import { useRegistrarPagoRapidoFactura } from '../../lib/mutaciones';
import type { Factura } from '../../lib/consultas';

interface Props {
  factura: Factura;
  alCerrar: () => void;
  alPagarExitoso?: () => void;
}

export function ModalPagarFacturaRapido({ factura, alCerrar, alPagarExitoso }: Props) {
  const avisos = useAvisos();
  const mutacionPagar = useRegistrarPagoRapidoFactura();

  const saldoPendiente =
    factura.saldoPendiente ??
    factura.situacion?.saldo ??
    Math.max(0, factura.montoTotal - (factura.montoPagado ?? factura.situacion?.montoPagado ?? 0));
  const montoPagado = factura.montoPagado ?? factura.situacion?.montoPagado ?? 0;

  const [pagarTotal, setPagarTotal] = useState<boolean>(true);
  const [montoAbono, setMontoAbono] = useState<number>(saldoPendiente);
  const [fechaPago, setFechaPago] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [medio, setMedio] = useState<'TRANSFERENCIA' | 'CHEQUE' | 'TARJETA_CREDITO' | 'TARJETA_DEBITO' | 'EFECTIVO' | 'OTRO'>('TRANSFERENCIA');
  const [referencia, setReferencia] = useState<string>('');
  const [notas, setNotas] = useState<string>('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saldoPendiente <= 0) {
      avisos.error('Esta factura ya no tiene saldo pendiente por pagar.');
      return;
    }

    const montoFinal = pagarTotal ? saldoPendiente : Number(montoAbono);
    if (!pagarTotal && (!montoFinal || montoFinal <= 0)) {
      avisos.error('Ingresa un monto de abono válido mayor a $0.');
      return;
    }
    if (!pagarTotal && montoFinal > saldoPendiente) {
      avisos.error(`El abono no puede ser mayor al saldo pendiente (${formatearCLP(saldoPendiente)}).`);
      return;
    }

    try {
      const res = await mutacionPagar.mutateAsync({
        id: factura.id,
        datos: {
          saldoTotal: pagarTotal,
          monto: pagarTotal ? undefined : montoFinal,
          fecha: fechaPago,
          medio: medio as any,
          referencia: referencia.trim() || undefined,
          notas: notas.trim() || undefined,
        },
      });

      avisos.exito(res.mensaje || `Pago de ${formatearCLP(montoFinal)} registrado con éxito.`);
      alPagarExitoso?.();
      alCerrar();
    } catch (err) {
      avisos.error(err instanceof Error ? err.message : 'Error al registrar el pago de la factura.');
    }
  };

  return (
    <div className="modal-overlay" onClick={alCerrar}>
      <div className="modal" style={{ maxWidth: 520, width: '100%' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 26,
                  height: 26,
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--accent-green-soft)',
                  color: 'var(--accent-green)',
                }}
              >
                <IconoDocumento size={14} />
              </span>
              <h2 style={{ fontSize: 17, fontWeight: 700, margin: 0, color: 'var(--text-primary)' }}>
                Registrar Pago de Factura
              </h2>
            </div>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '4px 0 0 34px' }}>
              Acción 1-Clic: Salda el 100% o ingresa un abono sin salir de la cartera.
            </p>
          </div>
          <button type="button" className="btn-icono" onClick={alCerrar} aria-label="Cerrar modal">
            <IconoCerrar size={15} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '16px 24px' }}>
          {/* Tarjeta Resumen Factura */}
          <div
            style={{
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-md)',
              padding: '14px 16px',
              marginBottom: 16,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <div>
                <span
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontWeight: 700,
                    fontSize: 14,
                    color: 'var(--text-primary)',
                    marginRight: 8,
                  }}
                >
                  {factura.numero ? `Folio #${factura.numero}` : 'Factura s/n'}
                </span>
                {factura.numeroOrdenCompra && (
                  <span style={{ fontSize: 11, color: 'var(--accent-blue)', fontWeight: 500 }}>
                    OC: {factura.numeroOrdenCompra}
                  </span>
                )}
              </div>
              <span
                className="td-mono"
                style={{
                  fontSize: 15,
                  fontWeight: 800,
                  color: saldoPendiente > 0 ? 'var(--accent-yellow)' : 'var(--accent-green)',
                }}
              >
                Saldo: {formatearCLP(saldoPendiente)}
              </span>
            </div>

            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              <strong>{factura.organismo?.nombre ?? 'Organismo Público'}</strong>
              {factura.organismo?.rut && <span style={{ color: 'var(--text-muted)' }}> ({factura.organismo.rut})</span>}
            </div>

            {factura.proyecto && (
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                Proyecto: <span style={{ color: 'var(--text-secondary)' }}>{factura.proyecto.codigo} · {factura.proyecto.nombre}</span>
              </div>
            )}

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 8,
                marginTop: 4,
                paddingTop: 8,
                borderTop: '1px solid var(--border-subtle)',
                fontSize: 11,
              }}
            >
              <div>
                <div style={{ color: 'var(--text-muted)' }}>Total Facturado</div>
                <div className="td-mono" style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                  {formatearCLP(factura.montoTotal)}
                </div>
              </div>
              <div>
                <div style={{ color: 'var(--text-muted)' }}>Ya Cobrado</div>
                <div className="td-mono" style={{ fontWeight: 600, color: 'var(--accent-green)' }}>
                  {formatearCLP(montoPagado)}
                </div>
              </div>
              <div>
                <div style={{ color: 'var(--text-muted)' }}>Mora Actual</div>
                <div
                  style={{
                    fontWeight: 600,
                    color: (factura.diasMora ?? 0) > 0 ? 'var(--accent-red)' : 'var(--text-muted)',
                  }}
                >
                  {(factura.diasMora ?? 0) > 0 ? `${factura.diasMora} días` : 'Al día'}
                </div>
              </div>
            </div>
          </div>

          {/* Modalidad de Liquidación */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8, display: 'block' }}>
              Modalidad de Pago
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <button
                type="button"
                onClick={() => {
                  setPagarTotal(true);
                  setMontoAbono(saldoPendiente);
                }}
                style={{
                  padding: '10px 12px',
                  borderRadius: 'var(--radius-sm)',
                  border: `1px solid ${pagarTotal ? 'var(--accent-green)' : 'var(--border-subtle)'}`,
                  background: pagarTotal ? 'var(--accent-green-soft)' : 'var(--bg-surface)',
                  color: pagarTotal ? 'var(--accent-green)' : 'var(--text-secondary)',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.15s ease',
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 12 }}>Liquidar 100%</div>
                <div style={{ fontSize: 11, opacity: 0.9, marginTop: 2 }}>{formatearCLP(saldoPendiente)}</div>
              </button>

              <button
                type="button"
                onClick={() => setPagarTotal(false)}
                style={{
                  padding: '10px 12px',
                  borderRadius: 'var(--radius-sm)',
                  border: `1px solid ${!pagarTotal ? 'var(--accent-blue)' : 'var(--border-subtle)'}`,
                  background: !pagarTotal ? 'var(--accent-blue-soft)' : 'var(--bg-surface)',
                  color: !pagarTotal ? 'var(--accent-blue)' : 'var(--text-secondary)',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.15s ease',
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 12 }}>Abono Parcial</div>
                <div style={{ fontSize: 11, opacity: 0.9, marginTop: 2 }}>Ingresar monto</div>
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {!pagarTotal && (
              <div className="form-group">
                <label htmlFor="pago_monto" style={{ fontSize: 12, fontWeight: 600 }}>
                  Monto a Abonar (CLP) *
                </label>
                <input
                  id="pago_monto"
                  type="number"
                  min={1}
                  max={saldoPendiente}
                  value={montoAbono}
                  onChange={(e) => setMontoAbono(Number(e.target.value))}
                  placeholder={`Máximo ${formatearCLP(saldoPendiente)}`}
                  required
                  style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}
                />
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label htmlFor="pago_fecha" style={{ fontSize: 12, fontWeight: 600 }}>
                  Fecha Efectiva *
                </label>
                <input
                  id="pago_fecha"
                  type="date"
                  value={fechaPago}
                  onChange={(e) => setFechaPago(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="pago_medio" style={{ fontSize: 12, fontWeight: 600 }}>
                  Medio de Pago *
                </label>
                <select
                  id="pago_medio"
                  value={medio}
                  onChange={(e) => setMedio(e.target.value as any)}
                >
                  <option value="TRANSFERENCIA">Transferencia Electrónica</option>
                  <option value="CHEQUE">Cheque / Vale Vista</option>
                  <option value="TARJETA_CREDITO">Tarjeta de Crédito</option>
                  <option value="TARJETA_DEBITO">Tarjeta de Débito</option>
                  <option value="EFECTIVO">Efectivo / Caja</option>
                  <option value="OTRO">Otro Medio</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="pago_ref" style={{ fontSize: 12, fontWeight: 600 }}>
                Referencia / N° Depósito SIGFE / Cartola
              </label>
              <input
                id="pago_ref"
                type="text"
                placeholder="Ej: Depósito TGR SIGFE #99104 - Banco Estado"
                value={referencia}
                onChange={(e) => setReferencia(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="pago_notas" style={{ fontSize: 12, fontWeight: 600 }}>
                Observaciones / Notas Internas
              </label>
              <textarea
                id="pago_notas"
                rows={2}
                placeholder="Detalles sobre retenciones, multas descontadas o confirmación bancaria..."
                value={notas}
                onChange={(e) => setNotas(e.target.value)}
              />
            </div>
          </div>

          <div className="modal-footer" style={{ padding: '16px 0 0', marginTop: 16 }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={alCerrar}
              disabled={mutacionPagar.isPending}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={mutacionPagar.isPending}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: 'var(--accent-green)',
                borderColor: 'var(--accent-green)',
              }}
            >
              <IconoCheck size={15} />
              <span>
                {mutacionPagar.isPending
                  ? 'Registrando...'
                  : pagarTotal
                  ? `Saldar 100% (${formatearCLP(saldoPendiente)})`
                  : `Abonar ${formatearCLP(montoAbono || 0)}`}
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
